import * as ort from 'onnxruntime-web';
import { formatModelResult } from './modelUtils';
import { runOrtInferenceSerialized } from './ortInferenceLock.js';
import { isMobileBrowser } from './deviceDetection';
import {
    imageDataToRgb,
    convertToGrayscaleFromRgb,
    resizeRgbWithPad,
} from '../services/opencvWorkerManager';

/**
 * Model input is 512²; decoding multi-megapixel canvases on mobile exhausts RAM.
 * Both iOS Safari and Android (Chrome, Samsung, etc.) share the same 1536px cap.
 * Desktop gets a more generous 2048px limit (still well above the 512px model input).
 */
const MOBILE_DECODE_MAX_LONG_EDGE = 1536;
const DESKTOP_DECODE_MAX_LONG_EDGE = 2048;

/** Downscale decoded bitmap before getImageData; closes original when shrunk. */
async function downscaleImageBitmapForPreprocess(bitmap) {
  const maxLong = isMobileBrowser()
    ? MOBILE_DECODE_MAX_LONG_EDGE
    : DESKTOP_DECODE_MAX_LONG_EDGE;
  const w0 = bitmap.width;
  const h0 = bitmap.height;
  const long0 = Math.max(w0, h0);
  if (long0 <= maxLong) return bitmap;

  const scale = maxLong / long0;
  const w1 = Math.max(2, Math.round(w0 * scale));
  const h1 = Math.max(2, Math.round(h0 * scale));

  let small;
  try {
    small = await createImageBitmap(bitmap, 0, 0, w0, h0, {
      resizeWidth: w1,
      resizeHeight: h1,
      resizeQuality: 'medium',
    });
  } catch {
    const c = document.createElement('canvas');
    c.width = w1;
    c.height = h1;
    c.getContext('2d').drawImage(bitmap, 0, 0, w1, h1);
    small = await createImageBitmap(c);
    c.width = 0;
    c.height = 0;
  }
  try {
    bitmap.close();
  } catch (_) {}
  return small;
}

export { imageDataToRgb, convertToGrayscaleFromRgb, resizeRgbWithPad } from '../services/opencvWorkerManager';

/**
 * Crop RGB buffer using grayscale mask (pixels with gray > tol keep rows/cols).
 * @param {Uint8ClampedArray|Float32Array} grayscaleValues
 * @returns {{ rgb: Uint8ClampedArray, width: number, height: number } | null}
 */
export function cropRgbFromMask(rgb, width, height, grayscaleValues, tol) {
    const rowMask = new Array(height).fill(false);
    const colMask = new Array(width).fill(false);

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if (grayscaleValues[y * width + x] > tol) {
                rowMask[y] = true;
                colMask[x] = true;
            }
        }
    }

    const rowIndices = rowMask.map((val, idx) => (val ? idx : -1)).filter((idx) => idx !== -1);
    const colIndices = colMask.map((val, idx) => (val ? idx : -1)).filter((idx) => idx !== -1);

    if (rowIndices.length === 0 || colIndices.length === 0) {
        return null;
    }

    const croppedWidth = colIndices.length;
    const croppedHeight = rowIndices.length;
    const croppedRgb = new Uint8ClampedArray(croppedWidth * croppedHeight * 3);

    for (let newY = 0; newY < croppedHeight; newY++) {
        const origY = rowIndices[newY];
        const origRowBase = origY * width;
        const newRowBase = newY * croppedWidth;
        for (let newX = 0; newX < croppedWidth; newX++) {
            const origX = colIndices[newX];
            const newIdx = (newRowBase + newX) * 3;
            const origIdx = (origRowBase + origX) * 3;
            croppedRgb[newIdx] = rgb[origIdx];
            croppedRgb[newIdx + 1] = rgb[origIdx + 1];
            croppedRgb[newIdx + 2] = rgb[origIdx + 2];
        }
    }
    return { rgb: croppedRgb, width: croppedWidth, height: croppedHeight };
}

function rgbToRgbaImageData(rgb, width, height) {
    const rgba = new Uint8ClampedArray(width * height * 4);
    for (let i = 0, j = 0; i < rgb.length; i += 3, j += 4) {
        rgba[j] = rgb[i];
        rgba[j + 1] = rgb[i + 1];
        rgba[j + 2] = rgb[i + 2];
        rgba[j + 3] = 255;
    }
    return new ImageData(rgba, width, height);
}

async function pngBlobFromRgb(rgb, width, height) {
    const imageData = rgbToRgbaImageData(rgb, width, height);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').putImageData(imageData, 0, 0);
    try {
        return await new Promise((resBlob, rejBlob) => {
            canvas.toBlob((blob) => (blob ? resBlob(blob) : rejBlob(new Error('toBlob failed'))), 'image/png', 1);
        });
    } finally {
        canvas.width = 0;
        canvas.height = 0;
    }
}

/**
 * Apply softmax to logits array
 * @param {Float32Array|Array} logits - Raw model output logits
 * @returns {Array} Probabilities after softmax
 */
export function softmax(logits) {
    const arr = Array.from(logits);
    const max = Math.max(...arr);
    const exp = arr.map(x => Math.exp(x - max));
    const sum = exp.reduce((a, b) => a + b, 0);
    return exp.map(x => x / sum);
}

/** Free WASM backing for every ort.Tensor in a session.run() results object. */
function disposeOrtResultTensors(results) {
    if (!results || typeof results !== 'object') return;
    for (const k of Object.keys(results)) {
        const v = results[k];
        if (v && typeof v.dispose === 'function') {
            try { v.dispose(); } catch (_) { /* ignore */ }
        }
    }
}

/**
 * Normalizes pixel data for model input
 * @param {Uint8ClampedArray|Uint8Array} pixelData - RGB pixel data
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @returns {Float32Array} - Normalized data in NCHW format
 */
function normalizeImage(pixelData, width, height) {
    const mean = [0.42731419, 0.25496882, 0.12074396];
    const std = [0.29390488, 0.19609516, 0.13065216];
    
    // Create output array in NCHW format: [1, 3, height, width]
    const normalizedData = new Float32Array(1 * 3 * height * width);
    
    // Process each channel separately
    for (let c = 0; c < 3; c++) {
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                // Input index in HWC format
                const inputIdx = (y * width + x) * 3 + c;
                // Output index in NCHW format
                const outputIdx = c * (height * width) + y * width + x;
                
                // Normalize: (pixel/255 - mean) / std
                const normalizedValue = ((pixelData[inputIdx] / 255.0) - mean[c]) / std[c];
                normalizedData[outputIdx] = normalizedValue;
            }
        }
    }
    
    return normalizedData;
}

/**
 * Preprocess image for model inference.
 * One canvas read to decode bitmap → RGB buffer; grayscale, crop, and OpenCV resize work on numbers only.
 * RGBA is built only where the browser requires it (PNG blobs via canvas).
 * @param {File} imageFile - The input image file
 * @param {{ skipPreviewBlobs?: boolean }} [options] - If skipPreviewBlobs, skip PNG blob generation (saves RAM; screening path only needs normalizedData).
 * @returns {Promise<{ normalizedData: Float32Array, processedImageBlob: Blob|null, croppedImageBlob: Blob|null }>} Preprocessed tensor and optional preview blobs
 */
export async function preprocessImage(imageFile, options = {}) {
    const skipPreviewBlobs = !!options.skipPreviewBlobs;
    let bitmap = await createImageBitmap(imageFile, {
        colorSpaceConversion: 'none',
        premultiplyAlpha: 'none'
    });
    bitmap = await downscaleImageBitmapForPreprocess(bitmap);
    try {
        let decodeCanvas = document.createElement('canvas');
        decodeCanvas.width = bitmap.width;
        decodeCanvas.height = bitmap.height;
        let decodeCtx = decodeCanvas.getContext('2d', { willReadFrequently: true });
        decodeCtx.drawImage(bitmap, 0, 0);
        // Pixels are now in the canvas — release the bitmap immediately.
        try { bitmap.close(); } catch (_) {}
        bitmap = null;
        let snapshot = decodeCtx.getImageData(0, 0, decodeCanvas.width, decodeCanvas.height);
        decodeCanvas.width = 0;
        decodeCanvas.height = 0;
        decodeCanvas = null;
        decodeCtx = null;
        const { width, height } = snapshot;
        
        let rgb = await imageDataToRgb(snapshot);
        snapshot.data.fill(0);
        snapshot = null;
        let grayscaleValues = await convertToGrayscaleFromRgb(rgb, width, height);
        const cropped = cropRgbFromMask(rgb, width, height, grayscaleValues, 15);
        grayscaleValues.fill(0);
        grayscaleValues = null;

        let workRgb = cropped ? cropped.rgb : rgb;
        const workW = cropped ? cropped.width : width;
        const workH = cropped ? cropped.height : height;
        if (cropped) {
            rgb.fill(0);             // rgb and workRgb are different arrays, wipe rgb
        }
        rgb = null;

        let croppedImageBlob = null;
        let processedImageBlob = null;
        if (!skipPreviewBlobs) {
            croppedImageBlob = await pngBlobFromRgb(workRgb, workW, workH);
        }
        let resizedRgb = await resizeRgbWithPad(workRgb, workW, workH, 512);
        workRgb = null;

        if (!skipPreviewBlobs) {
            processedImageBlob = await pngBlobFromRgb(resizedRgb, 512, 512);
        }
        const normalizedData = normalizeImage(resizedRgb, 512, 512);
        resizedRgb.fill(0);
        resizedRgb = null;

        return { normalizedData, processedImageBlob, croppedImageBlob };
    } finally {
        try {
            bitmap.close();
        } catch (_) {}
    }
}

/**
 * Run fundus detector inference
 * @param {Float32Array} preprocessedTensor - Preprocessed image tensor [1, 3, 512, 512]
 * @param {ort.InferenceSession} fundusSession - Fundus model ONNX session
 * @returns {Promise<Object>} { fundus_logits, fundus_score, fundus_label }
 */
export async function runFundusInference(preprocessedTensor, fundusSession) {
    try {
        // Check session validity
        if (!fundusSession) {
            throw new Error('Fundus model session is not available');
        }
        
        if (!fundusSession.inputNames || fundusSession.inputNames.length === 0) {
            throw new Error('Fundus model session is invalid. Input names are missing.');
        }
        
        if (!fundusSession.outputNames || fundusSession.outputNames.length === 0) {
            throw new Error('Fundus model session is invalid. Output names are missing.');
        }
        
        // Create tensor
        const tensor = new ort.Tensor('float32', preprocessedTensor, [1, 3, 512, 512]);
        const inputName = fundusSession.inputNames[0];
        const feeds = {};
        feeds[inputName] = tensor;
        
        // Run inference
        let results;
        try {
            results = await runOrtInferenceSerialized(() => fundusSession.run(feeds));
        } finally {
            if (tensor && typeof tensor.dispose === 'function') tensor.dispose();
        }
        try {
            const outputName = fundusSession.outputNames[0];
            const outputTensor = results[outputName];

            if (!outputTensor || !outputTensor.data) {
                throw new Error('Fundus model inference returned invalid results. Output tensor is missing or invalid.');
            }

            const logits = outputTensor.data;
            const probabilities = softmax(logits);
            const fundus_logits = Array.from(logits, Number);

            const labelIndex = probabilities[0] > probabilities[1] ? 0 : 1;
            const labelMapping = {
                0: "non_fundus",
                1: "fundus"
            };

            return {
                fundus_logits,
                fundus_score: probabilities,
                fundus_label: labelMapping[labelIndex]
            };
        } finally {
            disposeOrtResultTensors(results);
        }
    } catch (error) {
        // Capture inference errors in Sentry
        import('@sentry/react').then(Sentry => {
            Sentry.captureException(error, {
                tags: {
                    errorType: 'fundus_inference_error',
                    errorStage: 'image_processing',
                },
                extra: {
                    errorMessage: error.message,
                    errorName: error.name,
                    hasSession: !!fundusSession,
                    sessionInputNames: fundusSession?.inputNames,
                    sessionOutputNames: fundusSession?.outputNames,
                },
            });
        });
        
        throw new Error(`Fundus inference failed: ${error.message}`);
    }
}

/**
 * Run eyes detector inference
 * @param {Float32Array} preprocessedTensor - Preprocessed image tensor [1, 3, 512, 512]
 * @param {ort.InferenceSession} eyesSession - Eyes model ONNX session
 * @returns {Promise<Object>} { eyes_logits, eyes_score, eyes_label }
 */
export async function runEyesInference(preprocessedTensor, eyesSession) {
    try {
        // Check session validity
        if (!eyesSession) {
            throw new Error('Eyes model session is not available');
        }
        
        if (!eyesSession.inputNames || eyesSession.inputNames.length === 0) {
            throw new Error('Eyes model session is invalid. Input names are missing.');
        }
        
        if (!eyesSession.outputNames || eyesSession.outputNames.length === 0) {
            throw new Error('Eyes model session is invalid. Output names are missing.');
        }
        
        // Create tensor
        const tensor = new ort.Tensor('float32', preprocessedTensor, [1, 3, 512, 512]);
        const inputName = eyesSession.inputNames[0];
        const feeds = {};
        feeds[inputName] = tensor;
        
        // Run inference
        let results;
        try {
            results = await runOrtInferenceSerialized(() => eyesSession.run(feeds));
        } finally {
            if (tensor && typeof tensor.dispose === 'function') tensor.dispose();
        }
        try {
            const outputName = eyesSession.outputNames[0];
            const outputTensor = results[outputName];

            if (!outputTensor || !outputTensor.data) {
                throw new Error('Eyes model inference returned invalid results. Output tensor is missing or invalid.');
            }

            const logits = outputTensor.data;
            const probabilities = softmax(logits);
            const eyes_logits = Array.from(logits, Number);

            const labelIndex = probabilities[0] > probabilities[1] ? 0 : 1;
            const labelMapping = {
                0: "left_eye",
                1: "right_eye"
            };

            return {
                eyes_logits,
                eyes_score: probabilities,
                eyes_label: labelMapping[labelIndex]
            };
        } finally {
            disposeOrtResultTensors(results);
        }
    } catch (error) {
        // Capture inference errors in Sentry
        import('@sentry/react').then(Sentry => {
            Sentry.captureException(error, {
                tags: {
                    errorType: 'eyes_inference_error',
                    errorStage: 'image_processing',
                },
                extra: {
                    errorMessage: error.message,
                    errorName: error.name,
                    hasSession: !!eyesSession,
                    sessionInputNames: eyesSession?.inputNames,
                    sessionOutputNames: eyesSession?.outputNames,
                },
            });
        });
        
        throw new Error(`Eyes inference failed: ${error.message}`);
    }
}

/**
 * Calculate Mahalanobis distance
 * @param {Float32Array} features - Feature vector [1280]
 * @param {Float32Array} mean - Mean vector [1280]
 * @param {Float32Array} covinv - Inverse covariance matrix [1280, 1280]
 * @returns {number} Mahalanobis distance
 */
export function calculateMahalanobisDistance(features, mean, covinv) {
    // features: [1280]
    // mean: [1280]
    // covinv: [1280, 1280] (flattened)
    
    // Calculate diff = features - mean
    const diff = new Float32Array(1280);
    for (let i = 0; i < 1280; i++) {
        diff[i] = features[i] - mean[i];
    }
    
    // Calculate diff @ covinv (matrix-vector multiplication)
    // Result: [1280]
    const covinvDiff = new Float32Array(1280);
    for (let i = 0; i < 1280; i++) {
        let sum = 0;
        for (let j = 0; j < 1280; j++) {
            // covinv is stored as [row0, row1, ..., row1279]
            // covinv[i * 1280 + j] is element at row i, column j
            sum += covinv[i * 1280 + j] * diff[j];
        }
        covinvDiff[i] = sum;
    }
    
    // Calculate diff^T @ covinvDiff (dot product)
    let distanceSquared = 0;
    for (let i = 0; i < 1280; i++) {
        distanceSquared += diff[i] * covinvDiff[i];
    }
    
    // Return sqrt(distanceSquared)
    return Math.sqrt(Math.max(0, distanceSquared)); // Ensure non-negative
}

/**
 * Bin DR score to grade
 * @param {number} drScore - Raw DR score (0-4 range)
 * @returns {string} DR grade: "No DR", "Mild NPDR", "Moderate NPDR", "Severe NPDR", "PDR"
 */
export function binDRScore(drScore) {
    if (drScore < 0.5) {
        return "No DR";
    } else if (drScore < 1.5) {
        return "Mild NPDR";
    } else if (drScore < 2.5) {
        return "Moderate NPDR";
    } else if (drScore < 3.5) {
        return "Severe NPDR";
    } else {
        return "PDR";
    }
}

/**
 * Bin DME score to grade
 * @param {number} dmeScore - Raw DME score (0-1 range)
 * @returns {string} DME grade: "No DME", "DME"
 */
export function binDMEScore(dmeScore) {
    return dmeScore >= 0.5 ? "DME" : "No DME";
}

/** @type {Promise<{ mean: Float32Array, covinv: Float32Array }> | null} */
let mahalanobisFilesLoadPromise = null;

/**
 * Drop cached Mahalanobis mean/covinv (call when OPFS train feature files are replaced or purged).
 */
export function clearMahalanobisFilesCache() {
    mahalanobisFilesLoadPromise = null;
}

async function loadMahalanobisFilesFromStorage() {
    const { getTrainFeaturesMeanFromStorage, getTrainFeaturesCovinvFromStorage } = await import('../services/modelService');

    const meanBuffer = await getTrainFeaturesMeanFromStorage();
    const covinvBuffer = await getTrainFeaturesCovinvFromStorage();

    if (!meanBuffer || !covinvBuffer) {
        const missing = [];
        if (!meanBuffer) missing.push('train_features_mean.bin');
        if (!covinvBuffer) missing.push('train_features_covinv.bin');
        throw new Error(`Mahalanobis distance files not found: ${missing.join(', ')}. Please ensure they are downloaded during app startup.`);
    }

    const mean = new Float32Array(meanBuffer);
    const covinv = new Float32Array(covinvBuffer);

    return { mean, covinv };
}

/**
 * Load binary files from OPFS storage (single in-memory load per session; concurrent callers share one read).
 * @returns {Promise<Object>} { mean: Float32Array, covinv: Float32Array }
 */
export async function loadMahalanobisFiles() {
    if (!mahalanobisFilesLoadPromise) {
        mahalanobisFilesLoadPromise = loadMahalanobisFilesFromStorage().catch((err) => {
            mahalanobisFilesLoadPromise = null;
            throw err;
        });
    }
    return mahalanobisFilesLoadPromise;
}

/**
 * Run DRDME model inference
 * @param {Float32Array} preprocessedTensor - Preprocessed image tensor [1, 3, 512, 512]
 * @param {ort.InferenceSession} drdmeSession - DRDME model ONNX session
 * @returns {Promise<Object>} { features: Float32Array[1280], DR_score: number, DME_score: number, mahalanobis_distance: number, DR_grade: string, DME_grade: string }
 */
export async function runDRDMEInference(preprocessedTensor, drdmeSession) {
    try {
        // Check session validity
        if (!drdmeSession) {
            throw new Error('DRDME model session is not available');
        }
        
        if (!drdmeSession.inputNames || drdmeSession.inputNames.length === 0) {
            throw new Error('DRDME model session is invalid. Input names are missing.');
        }
        
        if (!drdmeSession.outputNames || drdmeSession.outputNames.length === 0) {
            throw new Error('DRDME model session is invalid. Output names are missing.');
        }
        
        // Create tensor
        const tensor = new ort.Tensor('float32', preprocessedTensor, [1, 3, 512, 512]);
        const inputName = drdmeSession.inputNames[0];
        const feeds = {};
        feeds[inputName] = tensor;
        
        // Run inference
        let results;
        try {
            results = await runOrtInferenceSerialized(() => drdmeSession.run(feeds));
        } finally {
            if (tensor && typeof tensor.dispose === 'function') tensor.dispose();
        }

        try {
        // DRDME model should return: features, DR_score, DME_score (same as Python)
        // Try to detect which output is which based on shape and name
        let featuresTensor, drScoreTensor, dmeScoreTensor;
        
        // First pass: Try to find by name (case-insensitive)
        for (const outputName of drdmeSession.outputNames) {
            const lowerName = outputName.toLowerCase();
            const tensor = results[outputName];
            
            if (!tensor) continue;
            
            // Features are typically a vector [1280] or [1, 1280] or [1280, 1]
            const isFeaturesShape = tensor.dims && (
                (tensor.dims.length === 1 && tensor.dims[0] === 1280) ||
                (tensor.dims.length === 2 && (tensor.dims[0] === 1 || tensor.dims[1] === 1) && 
                 (tensor.dims[0] === 1280 || tensor.dims[1] === 1280)) ||
                tensor.data.length === 1280
            );
            
            if ((lowerName.includes('feature') || isFeaturesShape) && !featuresTensor) {
                featuresTensor = tensor;
            } else if ((lowerName.includes('dr') || lowerName.includes('diabetic')) && !drScoreTensor) {
                drScoreTensor = tensor;
            } else if (lowerName.includes('dme') && !dmeScoreTensor) {
                dmeScoreTensor = tensor;
            }
        }
        
        // Second pass: If not found by name, use shape-based detection
        // Features: [1280] or [1, 1280] or [1280, 1] - large vector
        // Scores: [1] or [1, 1] - scalars
        
        if (!featuresTensor) {
            for (const outputName of drdmeSession.outputNames) {
                const tensor = results[outputName];
                if (!tensor) continue;
                
                // Check if this looks like features (1280 elements)
                const isFeaturesShape = tensor.dims && (
                    (tensor.dims.length === 1 && tensor.dims[0] === 1280) ||
                    (tensor.dims.length === 2 && (
                        (tensor.dims[0] === 1 && tensor.dims[1] === 1280) ||
                        (tensor.dims[0] === 1280 && tensor.dims[1] === 1)
                    )) ||
                    tensor.data.length === 1280
                );
                
                if (isFeaturesShape && !featuresTensor) {
                    featuresTensor = tensor;
                    break;
                }
            }
        }
        
        // Third pass: Use order-based assignment
        // ML Team confirmed: Model returns 3 outputs in order: features, DR_score, DME_score
        // Shape: [1, 1280], [1, 1], [1, 1]
        
        if (drdmeSession.outputNames.length === 3) {
            // 3 outputs: features, DR_score, DME_score (confirmed by ML team)
            // First output is features [1, 1280]
            if (!featuresTensor) {
                const firstOutput = results[drdmeSession.outputNames[0]];
                // Check if first output has 1280 elements (features)
                if (firstOutput && firstOutput.data && firstOutput.data.length === 1280) {
                    featuresTensor = firstOutput;
                } else {
                    // Try all outputs to find features by shape
                    for (const outputName of drdmeSession.outputNames) {
                        const tensor = results[outputName];
                        if (tensor && tensor.data && tensor.data.length === 1280) {
                            featuresTensor = tensor;
                            break;
                        }
                    }
                }
            }
            // Second output is DR_score [1, 1]
            if (!drScoreTensor && drdmeSession.outputNames.length >= 2) {
                drScoreTensor = results[drdmeSession.outputNames[1]];
            }
            // Third output is DME_score [1, 1]
            if (!dmeScoreTensor && drdmeSession.outputNames.length >= 3) {
                dmeScoreTensor = results[drdmeSession.outputNames[2]];
            }
        } else if (drdmeSession.outputNames.length === 2) {
            // 2 outputs: Check if one is features, or both are scores
            // Check all outputs for features shape
            for (const outputName of drdmeSession.outputNames) {
                const tensor = results[outputName];
                if (tensor && tensor.data && tensor.data.length === 1280 && !featuresTensor) {
                    featuresTensor = tensor;
                } else if (tensor && tensor.data && tensor.data.length === 1) {
                    // This is a score (DR or DME)
                    if (!drScoreTensor) {
                        drScoreTensor = tensor;
                    } else if (!dmeScoreTensor) {
                        dmeScoreTensor = tensor;
                    }
                }
            }
            
            // Fallback: if still no assignments, use order
            if (!drScoreTensor) {
                drScoreTensor = results[drdmeSession.outputNames[0]];
            }
            if (!dmeScoreTensor) {
                dmeScoreTensor = results[drdmeSession.outputNames[1]];
            }
        }
        
        // Validate DR score tensor
        if (!drScoreTensor || drScoreTensor.data === undefined) {
            throw new Error(`DRDME model inference returned invalid DR_score. Output tensor is missing or invalid. Available outputs: ${drdmeSession.outputNames.join(', ')}`);
        }
        
        // Validate DME score tensor
        if (!dmeScoreTensor || dmeScoreTensor.data === undefined) {
            dmeScoreTensor = { data: [0] };
        }
        
        // Extract scores (handle both scalar and array outputs)
        let DR_score, DME_score;
        
        if (Array.isArray(drScoreTensor.data)) {
            DR_score = drScoreTensor.data.length > 0 ? drScoreTensor.data[0] : 0;
        } else if (drScoreTensor.data instanceof Float32Array || drScoreTensor.data instanceof Float64Array) {
            DR_score = drScoreTensor.data.length > 0 ? drScoreTensor.data[0] : 0;
        } else {
            DR_score = drScoreTensor.data;
        }
        
        if (Array.isArray(dmeScoreTensor.data)) {
            DME_score = dmeScoreTensor.data.length > 0 ? dmeScoreTensor.data[0] : 0;
        } else if (dmeScoreTensor.data instanceof Float32Array || dmeScoreTensor.data instanceof Float64Array) {
            DME_score = dmeScoreTensor.data.length > 0 ? dmeScoreTensor.data[0] : 0;
        } else {
            DME_score = dmeScoreTensor.data !== undefined ? dmeScoreTensor.data : 0;
        }
        
        // Extract features (if available) - same as Python: features, DR_score, DME_score
        let features = null;
        if (featuresTensor && featuresTensor.data) {
            // Handle different feature tensor shapes: [1280], [1, 1280], [1280, 1]
            if (featuresTensor.data.length === 1280) {
                features = new Float32Array(featuresTensor.data);
            } else if (featuresTensor.dims && featuresTensor.dims.length === 2) {
                // Reshape if needed: [1, 1280] or [1280, 1] -> [1280]
                const flatFeatures = Array.from(featuresTensor.data);
                if (flatFeatures.length === 1280) {
                    features = new Float32Array(flatFeatures);
                }
            }
        }

        // Calculate Mahalanobis distance if features are available
        let mahalanobis_distance = null;
        if (features && features.length === 1280) {
            try {
                // Load Mahalanobis distance files (mean and covinv)
                const { mean, covinv } = await loadMahalanobisFiles();
                
                // Calculate Mahalanobis distance (similarity score)
                mahalanobis_distance = calculateMahalanobisDistance(features, mean, covinv);
            } catch {
                mahalanobis_distance = null;
            }
        }
        
        // Bin scores to grades
        const DR_grade = binDRScore(DR_score);
        const DME_grade = binDMEScore(DME_score);
        
        // Return same structure as Python: features, DR_score, DME_score
        return {
            features,           // Float32Array[1280] or null (same as Python)
            DR_score,           // number (0-4 range, same as Python)
            DME_score,          // number (0-1 range, same as Python)
            mahalanobis_distance, // number or null (similarity score)
            DR_grade,          // string (No DR, Mild NPDR, etc.)
            DME_grade          // string (No DME, DME)
        };
        } finally {
            disposeOrtResultTensors(results);
        }
    } catch (error) {
        // Capture inference errors in Sentry
        import('@sentry/react').then(Sentry => {
            Sentry.captureException(error, {
                tags: {
                    errorType: 'drdme_inference_error',
                    errorStage: 'image_processing',
                },
                extra: {
                    errorMessage: error.message,
                    errorName: error.name,
                    hasSession: !!drdmeSession,
                    sessionInputNames: drdmeSession?.inputNames,
                    sessionOutputNames: drdmeSession?.outputNames,
                },
            });
        });
        
        throw new Error(`DRDME inference failed: ${error.message}`);
    }
}
